import MuiTablePagination, {
  type TablePaginationProps as MuiTablePaginationProps,
} from '@mui/material/TablePagination';
import { forwardRef, type ReactElement, type SyntheticEvent, useState } from 'react';
import {
  paginationSelectMenuPlacement,
  resolvePaginationSelectMenuPlacement,
} from './paginationSelectMenuPlacement';
import { tablePaginationRootSx } from './tablePaginationChrome';
import { TablePaginationPageActions } from './TablePaginationPageActions';
import type { MenuProps as MuiMenuProps } from '@mui/material/Menu';

type TablePaginationBaseProps = Omit<MuiTablePaginationProps, 'component'>;
type TablePaginationSelectProps = NonNullable<
  NonNullable<MuiTablePaginationProps['slotProps']>['select']
>;

const PAGINATION_MENU_ITEM_HEIGHT_PX = 24;

const buildPaginationSelectProps = (
  selectProps: TablePaginationSelectProps | undefined,
  menuPlacement: Partial<MuiMenuProps>,
  onOpen: (event: SyntheticEvent) => void,
): TablePaginationSelectProps => ({
  ...selectProps,
  onOpen: (event: SyntheticEvent) => {
    onOpen(event);
    selectProps?.onOpen?.(event);
  },
  MenuProps: {
    ...selectProps?.MenuProps,
    ...menuPlacement,
  },
});

export const TablePaginationBase = forwardRef<HTMLDivElement, TablePaginationBaseProps>(
  (
    { ActionsComponent = TablePaginationPageActions, rowsPerPageOptions, slotProps, sx, ...props },
    ref,
  ): ReactElement => {
    const [rowsPerPageMenuPlacement, setRowsPerPageMenuPlacement] = useState(
      paginationSelectMenuPlacement,
    );

    const handleRowsPerPageSelectOpen = (event: SyntheticEvent): void => {
      setRowsPerPageMenuPlacement(
        resolvePaginationSelectMenuPlacement(
          event.currentTarget as HTMLElement,
          (rowsPerPageOptions?.length ?? 0) * PAGINATION_MENU_ITEM_HEIGHT_PX,
        ),
      );
    };

    return (
      <MuiTablePagination
        ActionsComponent={ActionsComponent}
        component="div"
        ref={ref}
        rowsPerPageOptions={rowsPerPageOptions}
        slotProps={{
          ...slotProps,
          select: buildPaginationSelectProps(
            slotProps?.select,
            rowsPerPageMenuPlacement,
            handleRowsPerPageSelectOpen,
          ),
        }}
        sx={
          (sx
            ? [tablePaginationRootSx, sx]
            : [tablePaginationRootSx]) as MuiTablePaginationProps['sx']
        }
        {...props}
      />
    );
  },
);

TablePaginationBase.displayName = 'TablePaginationBase';
